/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {Route} from '@/routes/RouteDefinitions';

/**
 * Provides a reference to all pre-defined CTC Admin Application Features.
 * Each Feature is issued a unique Id which is used across all environments.
 *
 * The name of each feature is arbitrary and used for natural reference,
 * whereas the value of each feature represents the absolute Id of the
 * Feature.
 *
 * Each Feature id is defined, and assigned to a Feature within the CTC Admin
 * (CTC Admin) Application Entity's extendedAttributes value in each
 * environment.
 */
export const DASHBOARD_PAGE = '';

/**
 * The Features API provides the single, authoritative point of entry for initializing,
 * validating, and determining CTC Admin Feature  availability for authenticated Users
 * based on the Features available to the User's CTC Admin Roles.
 *
 * CTC Admin Features are defined within the features.json document of the doctc-ui-seed
 * project located at:
 *
 * @see http://bitbucket.pd.calampcorp.com:7990/projects/CTC Admin/repos/doctc-ui-seed/browse
 * @see http://bitbucket.pd.calampcorp.com:7990/repos/doctc-ui-seed/browse/src/json/features.json
 *
 *
 * ```typescript
 *
 *   import Features from '@/security';
 *
 *   [LOAD_ROLES_RESPONSE]: (draft, {roles: Role[]}) => {
 *     Features.initialize({
 *       user        : draft.user,
 *       allFeatures : draft.features,
 *       appKey      : get('appKey'),
 *       roles
 *     });
 *     ...
 *   }
 *
 * ```
 *
 * @class Features
 */
class Features extends ApplicationFeatures {
  /**
   * Returns the default route for restricted accounts if the current account
   * is restricted, otherwise, returns the specified route.
   *
   * ```typescript
   *
   * import Features from '@/security/Features';
   *
   * const route = Features.getDefaultRoute(Route.TRACKING);
   *
   * ```
   *
   */
  getDefaultRoute(defaultRoute?: Route): Route {
    return defaultRoute || Route.DASHBOARD;
  }
}
export default new Features();
